import { remoteComponentPublish } from "../../../remote-trigger/browser";
import { MODAL_HIDE_EVENT } from "../../../modal/events";
import PinDropMap from "./pin-drop-map";
import MapsService, {
  MapProviderType
} from "@churchofjesuschrist/maps-service";

const BROWSER_UNSUPPORTED_CODE = 0;
const PERMISSION_DENIED_CODE = 1;

export default class PinDropModalController {
  constructor(component) {
    this.component = component;
    this.pinDropMap = null;
    this.currentAddress = null;
    this.currentCoordinates = null;
    this.currentCountry = null;
    this.currentIsValid = true; // Start as valid
    this.lastAddressSearchQuery = "";
    this.activeSearchQuery = "";
    this.parentForm = component.element.closest("form");
    this.handleModalEnterKey = this.handleModalEnterKey.bind(this);
    this.preventParentSubmitWhileOpen =
      this.preventParentSubmitWhileOpen.bind(this);
    this.preventParentSubmitButtonClick =
      this.preventParentSubmitButtonClick.bind(this);
    this.modalGuardsInitialized = false;
    this.locationFieldHandlersElement = null;
  }

  show({
    initialCoordinates,
    precision,
    initialZoom,
    initialAddress,
    initialCountry,
    initialIsValid = false,
    initialErrorMessage,
    onAccept,
    onGeolocateFallback
  }) {
    // Store callbacks and precision
    this.onAccept = onAccept;
    this.onGeolocateFallback = onGeolocateFallback;
    this.precision = precision || "address"; // Default to address-level
    this.initialZoom = initialZoom;
    this.initialAddress = initialAddress; // Store initial address from address block
    this.lastAddressSearchQuery = initialIsValid
      ? initialAddress?.trim() || ""
      : "";
    this.currentCoordinates = initialCoordinates || null;
    this.currentAddress = initialIsValid ? initialAddress : null;
    this.currentCountry = initialIsValid ? initialCountry || null : null;
    this.currentIsValid = initialIsValid;

    // Set up button handlers
    this.setupButtonHandlers();

    this.updateAddressDisplay(initialAddress);

    // Open the modal
    const elem = document.createElement("div");
    elem.dataset.remoteTriggerId =
      this.component.element.dataset.pinDropModalRemoteTrigger;
    remoteComponentPublish(elem);

    this.onModalOpen(() => {
      this.initializeMap(initialCoordinates);
      this.setupLocationFieldHandlers();
      this.setupDialogAccessibility();
      this.updateValidationState(initialIsValid, initialErrorMessage);

      // Populate the location search field with the initial address
      const locationInput = this.component.element.querySelector(
        "[data-location-field]"
      );
      if (locationInput) {
        locationInput.value = initialAddress || "";
      }
    });
  }

  // Runs work when the remote-triggered modal has actually moved into its open state.
  onModalOpen(callback) {
    const modalContainer = this.getModalContainer();

    if (!modalContainer) {
      callback();
      return;
    }

    if (this.isModalOpen()) {
      callback();
      return;
    }

    const runWhenOpen = () => {
      if (!this.isModalOpen()) {
        return;
      }

      observer.disconnect();
      requestAnimationFrame(callback);
    };

    const observer = new MutationObserver(runWhenOpen);

    observer.observe(modalContainer, {
      attributes: true,
      attributeFilter: ["aria-hidden"]
    });

    requestAnimationFrame(runWhenOpen);
  }

  // Wires modal action buttons to the callbacks supplied by the address block.
  setupButtonHandlers() {
    const yesButton = this.component.element
      .querySelector("[data-pin-drop-yes]")
      ?.querySelector("button");

    if (yesButton) {
      yesButton.onclick = () => {
        this.acceptPinLocation();
      };
    }
  }

  // Accepts the currently selected pin location when it passes validation.
  acceptPinLocation() {
    if (
      !this.currentAddress ||
      !this.currentCoordinates ||
      !this.currentIsValid
    ) {
      return;
    }

    if (this.component.pinDropAddressConfirmed) {
      this.component.pinDropAddressConfirmed.value = "true";
    }
    this.onAccept?.(
      this.currentAddress,
      this.currentCoordinates,
      this.currentCountry
    );
    this.close();
  }

  // Wires the modal search field so typed searches update the map and validation.
  setupLocationFieldHandlers() {
    const searchButton =
      this.component.element.querySelector("[map-search-btn]");
    const locationInput = this.component.element.querySelector(
      "[data-location-field]"
    );

    if (locationInput && this.locationFieldHandlersElement === locationInput) {
      return;
    }

    this.setupModalGuards();

    // Allow user to manually edit the shared location field
    if (locationInput) {
      locationInput.addEventListener("input", () => {
        this.currentAddress = null;
        this.currentCountry = null;
        this.currentIsValid = false;
        this.lastAddressSearchQuery = "";
        this.setPendingValidationState();
      });

      locationInput.addEventListener(
        "keydown",
        async (event) => {
          if (!["Enter", "Tab"].includes(event.key)) {
            return;
          }

          const query = locationInput.value.trim();
          if (!query || query === this.lastAddressSearchQuery) {
            return;
          }

          if (event.key === "Enter") {
            event.preventDefault();
          }

          event.stopPropagation();
          event.stopImmediatePropagation();

          this.lastAddressSearchQuery = query;

          await this.searchLocation(query);
        },
        true
      );
    }

    if (searchButton && locationInput) {
      // Remove existing handlers and add new ones with capture phase to intercept first
      const handleSearch = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const query = locationInput.value.trim();
        if (!query || query === this.lastAddressSearchQuery) {
          return;
        }

        this.lastAddressSearchQuery = query;
        await this.searchLocation(query);
      };

      // Use capture phase to intercept before location field's handlers
      searchButton.addEventListener("click", handleSearch, true);
    }

    if (locationInput) {
      this.locationFieldHandlersElement = locationInput;
    }
  }

  // Prevents modal keyboard actions from submitting the parent address form.
  setupModalGuards() {
    if (this.modalGuardsInitialized) {
      return;
    }

    const modalContent = this.component.element.querySelector(
      '[data-type="pin-drop-modal"]'
    );
    const parentSubmitButtons =
      this.parentForm?.querySelectorAll('[type="submit"]') || [];

    ["keydown", "keypress", "keyup"].forEach((eventName) => {
      modalContent?.addEventListener(eventName, this.handleModalEnterKey, true);
    });
    this.parentForm?.addEventListener(
      "submit",
      this.preventParentSubmitWhileOpen,
      true
    );
    parentSubmitButtons.forEach((button) => {
      button.addEventListener(
        "click",
        this.preventParentSubmitButtonClick,
        true
      );
    });
    this.modalGuardsInitialized = true;
  }

  // Adds accessible dialog metadata that is specific to the pin-drop modal.
  setupDialogAccessibility() {
    const dialog = this.getModalContainer()?.querySelector(
      '[data-type="dialog"]'
    );
    const headingText =
      this.component.element
        .querySelector('[data-type="pin-drop-modal"] [data-type="heading"]')
        ?.textContent?.trim() || "Find your address";

    dialog?.setAttribute("aria-modal", "true");
    dialog?.setAttribute("aria-label", headingText);
  }

  // Finds the outer modal wrapper that owns the open/closed state.
  getModalContainer() {
    return this.component.element
      .querySelector('[data-type="pin-drop-modal"]')
      ?.closest('[data-type="modal"]');
  }

  // Checks whether the pin-drop modal is currently visible.
  isModalOpen() {
    const modalContainer = this.getModalContainer();

    return Boolean(
      modalContainer && modalContainer.getAttribute("aria-hidden") !== "true"
    );
  }

  // Blocks Enter from bubbling out of modal inputs that are not the search field.
  handleModalEnterKey(event) {
    if (event.key !== "Enter" || !this.isModalOpen()) {
      return;
    }

    const modalContent = this.component.element.querySelector(
      '[data-type="pin-drop-modal"]'
    );
    const activeElement = document.activeElement;
    const target = event.target;
    const isPinDropSearchField = target?.matches?.("[data-location-field]");
    const isInsideModal =
      modalContent &&
      (modalContent.contains(target) || modalContent.contains(activeElement));
    const isTextInput =
      (target instanceof HTMLInputElement &&
        !["button", "submit", "checkbox", "radio"].includes(target.type)) ||
      activeElement instanceof HTMLInputElement;

    if (!isInsideModal || !isTextInput || isPinDropSearchField) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // Stops parent form submits while the pin-drop modal is active.
  preventParentSubmitWhileOpen(event) {
    if (!this.isModalOpen()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // Stops parent submit-button clicks while the pin-drop modal is active.
  preventParentSubmitButtonClick(event) {
    if (!this.isModalOpen()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // Geocodes a typed query, then moves the pin when the result passes country rules.
  async searchLocation(query) {
    try {
      this.activeSearchQuery = query;
      const countryContext = this.component.getSelectedCountryContext?.() || {};

      const searchQuery =
        this.component.buildPinDropGeocodeQuery?.(query, countryContext) ||
        query;

      // Geocode the search query
      const results = await MapsService.geocodeAddress(searchQuery, {
        providers: [MapProviderType.GOOGLE],
        limit: 1,
        language: this.component.mapsLanguage,
        unconverted: true
      });

      if (results && results.length > 0 && results[0].coordinates) {
        const result = results[0];

        const resultMatchesQueryContext =
          this.component.pinDropResultMatchesQueryContext?.(
            result,
            query,
            countryContext
          );

        if (!resultMatchesQueryContext) {
          this.handleInvalidSearchResult(query);
          return;
        }

        const coordinates = {
          latitude: result.coordinates[1],
          longitude: result.coordinates[0],
          originalAddress: query // Store original query
        };

        // Update the map with new location
        if (this.pinDropMap) {
          // Remove location marker
          if (this.pinDropMap.locationMarker) {
            this.pinDropMap.locationMarker.setMap(null);
            this.pinDropMap.locationMarker = null;
          }

          // Place pin at new location
          this.pinDropMap.placePin({
            lat: coordinates.latitude,
            lng: coordinates.longitude
          });
          this.pinDropMap.initialAddress = null;
          this.pinDropMap.setZoomForGeocodeResult(result);

          this.pinDropMap.useGeocodeResult(result, coordinates);
        }
      } else {
        const resultAddress = results?.[0]
          ? PinDropMap.formatAddress(results[0])
          : "";
        this.handleInvalidSearchResult(query || resultAddress);
      }
    } catch (error) {
      PinDropMap.logMapError("Unable to search pin drop location", error);
      this.handleInvalidSearchResult(query);
    } finally {
      this.activeSearchQuery = "";
    }
  }

  // Creates the map instance and connects map callbacks back to modal state.
  async initializeMap(initialCoordinates) {
    let mapContainer;
    try {
      mapContainer = this.component.element.querySelector(
        "[data-pin-drop-map-container]"
      );

      if (!mapContainer) {
        return;
      }

      await PinDropModalController.waitForElementLayout(mapContainer);

      // Pass configuration from the AddressBlock component
      const mapConfig = {
        language: this.component.mapsLanguage,
        production: this.component.mapsIsProd,
        precision: this.precision,
        initialZoom: this.initialZoom,
        // Pass original address if available.
        initialAddress:
          this.initialAddress || initialCoordinates?.originalAddress,
        mapErrorMessage: this.component.pinDropMapError?.value
      };

      this.pinDropMap = new PinDropMap(
        mapContainer,
        initialCoordinates,
        mapConfig
      );

      // Set up the pin drop callback
      this.pinDropMap.onPinDropped = (
        address,
        coordinates,
        isValid = true,
        country = null
      ) => {
        this.currentAddress = isValid ? address : null;
        this.currentCoordinates = coordinates;
        this.currentCountry = isValid ? country : null;
        this.currentIsValid = isValid;
        this.lastAddressSearchQuery = "";
        if (isValid) {
          this.updateAddressDisplay(address);
        } else if (this.activeSearchQuery) {
          this.updateAddressDisplay(this.activeSearchQuery);
        } else {
          this.updateAddressDisplay("");
        }
        this.updateValidationState(isValid);
      };
      this.pinDropMap.onGeolocateError = async (error) => {
        let message = this.component.geolocateLookupError?.value;

        if (error?.code === PERMISSION_DENIED_CODE) {
          message = this.component.geolocateUserBlocked?.value || message;
        } else if (error?.code === BROWSER_UNSUPPORTED_CODE) {
          message =
            this.component.geolocateBrowserUnsupported?.value || message;
        }

        const fallbackHandled = await this.tryGeolocateFallback();
        if (fallbackHandled) {
          return;
        }

        this.currentIsValid = false;
        this.lastAddressSearchQuery = "";
        this.updateValidationState(false, message);
      };
    } catch (error) {
      // Show error message in the map container if it exists
      if (mapContainer) {
        PinDropModalController.renderMapLoadError(
          mapContainer,
          this.component.pinDropMapError?.value
        );
      }
    }
  }

  // Renders a simple fallback panel when the map cannot be initialized.
  static renderMapLoadError(mapContainer, message) {
    mapContainer.replaceChildren();

    const wrapper = document.createElement("div");
    wrapper.style.display = "flex";
    wrapper.style.alignItems = "center";
    wrapper.style.justifyContent = "center";
    wrapper.style.height = "100%";
    wrapper.style.background = "#f5f5f5";
    wrapper.style.color = "#666";
    wrapper.style.textAlign = "center";
    wrapper.style.padding = "20px";
    wrapper.textContent =
      PinDropMap.firstString(message) ||
      "Map failed to load. Please refresh the page.";

    mapContainer.appendChild(wrapper);
  }

  // Resolves once ResizeObserver reports visible dimensions for Google Maps.
  static waitForElementLayout(element) {
    return new Promise((resolve) => {
      if (element.offsetWidth && element.offsetHeight) {
        resolve();
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width && entry.contentRect.height) {
            observer.disconnect();
            resolve();
            return;
          }
        }
      });

      observer.observe(element);
    });
  }

  // Writes the current display value into the shared pin-drop search field.
  updateAddressDisplay(address) {
    const addressInput = this.component.element.querySelector(
      "[data-location-field]"
    );
    if (addressInput) {
      addressInput.value = address || "";
    }
  }

  // Marks the current search invalid while preserving the user's typed query.
  handleInvalidSearchResult(searchQuery = "") {
    this.currentAddress = null;
    this.currentCountry = null;
    this.currentIsValid = false;
    this.lastAddressSearchQuery = "";

    if (searchQuery) {
      this.updateAddressDisplay(searchQuery);
    }

    this.updateValidationState(
      false,
      this.component.pinDropModalInvalidLocationMessage?.value
    );
  }

  // Falls back to IP-based location when browser geolocation is unavailable.
  async tryGeolocateFallback() {
    if (!this.onGeolocateFallback || !this.pinDropMap) {
      return false;
    }

    try {
      const fallbackCandidate = await this.onGeolocateFallback();

      if (!fallbackCandidate?.coordinates) {
        PinDropMap.logMapError(
          "Unable to fall back to IP location after map geolocate failure"
        );
        return false;
      }

      this.pinDropMap.placePin(fallbackCandidate.coordinates);
      this.currentCoordinates = fallbackCandidate.coordinates;
      this.currentCountry = fallbackCandidate.isSaveableAddressLocation
        ? PinDropMap.getGeocodeResultCountry(fallbackCandidate.raw)
        : null;
      this.currentAddress = fallbackCandidate.isSaveableAddressLocation
        ? fallbackCandidate.address
        : null;
      this.currentIsValid =
        fallbackCandidate.isSaveableAddressLocation &&
        Boolean(fallbackCandidate.address);
      this.lastAddressSearchQuery = "";
      this.updateAddressDisplay(
        this.currentIsValid ? fallbackCandidate.address : ""
      );
      this.updateValidationState(this.currentIsValid);
      return true;
    } catch (error) {
      PinDropMap.logMapError(
        "Unable to fall back to IP location after map geolocate failure",
        error
      );
      return false;
    }
  }

  // Updates the save button and warning message to match location validity.
  updateValidationState(isValid, errorMessage) {
    // Find the Yes button and error container
    const yesButtonContainer = this.component.element.querySelector(
      "[data-pin-drop-yes]"
    );
    const yesButton = yesButtonContainer?.querySelector('[data-type="button"]');
    const errorContainer = this.component.element.querySelector(
      "[data-pin-drop-error]"
    );
    const errorText = errorContainer?.querySelector(
      "[data-pin-drop-error-text]"
    );

    if (!yesButton) {
      return;
    }

    if (!errorContainer) {
      return;
    }

    const addressInput = this.component.element.querySelector(
      "[data-location-field]"
    );

    if (isValid) {
      // Valid location - enable button and hide error
      yesButton.disabled = false;
      addressInput?.setAttribute("aria-invalid", "false");
      yesButton.style.removeProperty("background-color");
      yesButton.style.removeProperty("color");
      errorContainer.style.display = "none";
      errorContainer.setAttribute("aria-hidden", "true");
      if (errorText) {
        errorText.textContent =
          this.component.pinDropModalInvalidLocationMessage?.value || "";
      }
    } else {
      // Invalid location - disable button and show error
      // Use Grey40 background color for disabled state, keep original text color
      yesButton.disabled = true;
      addressInput?.setAttribute("aria-invalid", "true");
      yesButton.style.backgroundColor = "var(--grey-40)";
      yesButton.style.color = "var(--theme-opposed)"; // Keep white text
      if (errorText && errorMessage) {
        errorText.textContent = errorMessage;
      }
      errorContainer.style.display = "block";
      errorContainer.setAttribute("aria-hidden", "false");
    }
  }

  // Disables save while a user-edited search value has not been resolved yet.
  setPendingValidationState() {
    const yesButtonContainer = this.component.element.querySelector(
      "[data-pin-drop-yes]"
    );
    const yesButton = yesButtonContainer?.querySelector('[data-type="button"]');
    const errorContainer = this.component.element.querySelector(
      "[data-pin-drop-error]"
    );

    if (!yesButton || !errorContainer) {
      return;
    }

    yesButton.disabled = true;
    this.component.element
      .querySelector("[data-location-field]")
      ?.setAttribute("aria-invalid", "false");
    yesButton.style.backgroundColor = "var(--grey-40)";
    yesButton.style.color = "var(--theme-opposed)";
    errorContainer.style.display = "none";
    errorContainer.setAttribute("aria-hidden", "true");
  }

  // Destroys map resources and closes the modal.
  close() {
    // Clean up the map
    if (this.pinDropMap && this.pinDropMap.destroy) {
      this.pinDropMap.destroy();
    }
    this.pinDropMap = null;
    this.currentAddress = null;
    this.currentCoordinates = null;
    this.currentCountry = null;
    this.currentIsValid = true;
    this.lastAddressSearchQuery = "";
    this.activeSearchQuery = "";

    this.getModalContainer()?.dispatchEvent(new CustomEvent(MODAL_HIDE_EVENT));
  }
}
